/**
 * Google Analytics only for the marketing site, never inside `/app/*` (02-REVIEW CR-05).
 *
 * Why this is more than «load it from the marketing layout»: in a Next.js SPA a script loaded on
 * a marketing page survives client-side navigation into `/app`, and GA4 enhanced measurement
 * sends a page_view with page_title and path on every history change. Inside the app that means
 * patient ids in the URL and, potentially, patient names in the title: PHI sent to a third party.
 * Three layers keep that from happening:
 *  1. GA is never loaded while the current path is under `/app`.
 *  2. Once GA is in the document, a link into `/app` becomes a full document navigation, so the
 *     GA code is discarded with the marketing document instead of running inside the app.
 *  3. For every other same-document entry (router.push, Back/Forward) Google's documented opt-out
 *     flag is set before GA sees the history change, so no hit leaves.
 */

export const ID_MEDICION_GA = 'G-WDFR876Y79';
export const CLAVE_DESACTIVAR_GA = `ga-disable-${ID_MEDICION_GA}`;
const ID_SCRIPT_GA = 'script-analitica-google';
const URL_SCRIPT_GA = `https://www.googletagmanager.com/gtag/js?id=${ID_MEDICION_GA}`;
const BANDERA_GUARDIA = '__guardiaAnaliticaAppInstalada';

type VentanaConAnalitica = Window & Record<string, unknown> & {
    dataLayer?: unknown[];
    gtag?: (...argumentos: unknown[]) => void;
};

function ventana(): VentanaConAnalitica {
    return window as unknown as VentanaConAnalitica;
}

export function esRutaApp(ruta: string): boolean {
    return ruta === '/app' || ruta.startsWith('/app/');
}

function rutaDe(url: string | URL): string {
    return new URL(String(url), window.location.href).pathname;
}

export function sincronizarDesactivacionGA(ruta: string): void {
    ventana()[CLAVE_DESACTIVAR_GA] = esRutaApp(ruta);
}

export function analiticaGoogleCargada(): boolean {
    return typeof ventana().gtag === 'function';
}

export function cargarAnaliticaGoogle(): void {
    if (esRutaApp(window.location.pathname) || document.getElementById(ID_SCRIPT_GA)) return;
    const v = ventana();
    v.dataLayer = v.dataLayer || [];
    // gtag.js reads `arguments` objects from dataLayer, not arrays, so this cannot be a rest param.
    v.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params
        v.dataLayer!.push(arguments);
    };
    v.gtag('js', new Date());
    v.gtag('config', ID_MEDICION_GA);
    const script = document.createElement('script');
    script.id = ID_SCRIPT_GA;
    script.async = true;
    script.src = URL_SCRIPT_GA;
    document.head.appendChild(script);
}

// Installed before GA loads, so GA's own history wrapper ends up outside this one and its
// popstate listener after this one: the flag is always set before GA reacts.
function envolverHistorial(metodo: 'pushState' | 'replaceState'): void {
    const original = window.history[metodo];
    window.history[metodo] = function envuelto(this: History, estado: unknown, titulo: string, url?: string | URL | null) {
        if (url !== undefined && url !== null) sincronizarDesactivacionGA(rutaDe(url));
        return original.call(this, estado, titulo, url);
    };
}

function enlaceDeClic(evento: MouseEvent): HTMLAnchorElement | null {
    if (evento.defaultPrevented || evento.button !== 0) return null;
    if (evento.metaKey || evento.ctrlKey || evento.shiftKey || evento.altKey) return null;
    const enlace = (evento.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
    if (!enlace || (enlace.target && enlace.target !== '_self') || enlace.hasAttribute('download')) return null;
    return enlace;
}

function crearManejadorClic(navegar: (url: string) => void) {
    return (evento: MouseEvent) => {
        const enlace = enlaceDeClic(evento);
        if (!enlace || !analiticaGoogleCargada()) return;
        const destino = new URL(enlace.href, window.location.href);
        if (destino.origin !== window.location.origin || !esRutaApp(destino.pathname)) return;
        evento.preventDefault();
        evento.stopPropagation();
        navegar(destino.href);
    };
}

export interface OpcionesGuardiaAnalitica {
    navegar?: (url: string) => void;
}

export function instalarGuardiaAnaliticaApp({ navegar = (url) => window.location.assign(url) }: OpcionesGuardiaAnalitica = {}): void {
    const v = ventana();
    if (v[BANDERA_GUARDIA]) return;
    v[BANDERA_GUARDIA] = true;
    sincronizarDesactivacionGA(window.location.pathname);
    envolverHistorial('pushState');
    envolverHistorial('replaceState');
    window.addEventListener('popstate', () => sincronizarDesactivacionGA(window.location.pathname));
    document.addEventListener('click', crearManejadorClic(navegar), true);
}
